import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ForbiddenError, NotFoundError } from '@/api/errors';
import { Alert } from '@/app/components/ui/Alert';
import { Button } from '@/app/components/ui/Button';
import { Skeleton, type SkeletonKind } from '@/app/components/ui/Skeleton';
import type { AsyncState } from '@/lib/useAsync';

/**
 * Renders an async fetch's three states. Errors get a human sentence — in
 * particular a 403, which can always arrive even for hidden actions, reads as
 * "not allowed" rather than a broken page.
 */
export function AsyncSection<T>({
  state,
  label,
  skeleton = 'lines',
  children,
}: {
  state: AsyncState<T>;
  /** What is being loaded, for the loading/error sentences — an
   * already-translated noun phrase the caller supplies via `t()`. */
  label: string;
  /** Which skeleton shape to show while loading — matches the content that
   * will replace it. */
  skeleton?: SkeletonKind;
  children: (data: T) => ReactNode;
}) {
  const { t } = useTranslation();

  if (state.loading) {
    return <Skeleton kind={skeleton} />;
  }

  if (state.error) {
    const message =
      state.error instanceof ForbiddenError
        ? t('errors.forbidden', { label })
        : state.error instanceof NotFoundError
          ? t('errors.notFound', { label })
          : t('errors.loadingFailed', { label, message: state.error.message });
    return (
      <Alert tone="danger">
        <p>{message}</p>
        {!(state.error instanceof ForbiddenError) && (
          <Button variant="secondary" size="sm" className="mt-3" onClick={state.reload}>
            {t('common.tryAgain')}
          </Button>
        )}
      </Alert>
    );
  }

  return <>{children(state.data as T)}</>;
}
